import {makeStyles} from "@material-ui/core"
import {ModernTheme} from "../../../../../theme/modern/tokens"
import {COARSE, TOUCH_MIN} from "../../../../../theme/modern/touchTargets"

/**
 * Ported from `record.css` `.rec-shell` / `.main` / `.topinfo` and `ecg.css`
 * `.stage`.
 *
 * The shell is `100vh - appBarHeight`, NOT `100vh`: <ModernAppBar/> is a
 * sibling inside <ModernShell/>'s flex column, so a full-viewport height here
 * would overflow the page by exactly the bar height and reintroduce a document
 * scrollbar on every record.
 */
export default makeStyles((theme: ModernTheme) => ({
    shell: {
        height: `calc(100vh - ${theme.modern.appBarHeight}px)`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: theme.modern.bg,

        // narrow viewports fall back to a normal scrolling document — the app
        // bar wraps below 640 px, so the calc above stops being accurate
        "@media (max-width:820px)": {
            height: "auto",
            minHeight: 0,
            overflow: "visible",
        },
    },
    shell__mobile: {
        height: "auto",
        minHeight: 0,
        overflow: "visible",
        flex: 1,
    },
    main: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,

        "@media (max-width:820px)": {flex: "none"},
    },
    /**
     * Annotation layout: the beat surface and nothing else.
     *
     * It manages its own internal scrolling — overview, trace, boundary bar and
     * controls each hold their place while only the leads move — so this must
     * not scroll too, or the controls slide off the bottom just as someone
     * reaches for them.
     */
    main__annotation: {
        overflow: "hidden",

        "@media (max-width:820px)": {flex: 1},
    },
    /** Sidebar layout: rail on the left, trace + disposition dock on the right. */
    main__sidebar: {
        flexDirection: "row",

        "@media (max-width:820px)": {flexDirection: "column"},
    },

    // ---- sidebar layout ----
    rail: {
        width: 312,
        flex: "none",
        display: "flex",
        flexDirection: "column",
        gap: "7px",
        padding: 7,
        background: theme.modern.bg,
        borderRight: `1px solid ${theme.modern.line}`,
        overflowY: "auto",

        // cards size to their content in the rail; the rail itself scrolls
        "& > *": {flex: "none"},

        "@media (max-width:820px)": {
            width: "auto",
            maxHeight: "38vh",
            borderRight: "none",
            borderBottom: `1px solid ${theme.modern.line}`,
        },
    },
    stage_column: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        minHeight: 0,
    },
    dock: {
        flex: "none",
        maxHeight: "46%",
        overflow: "auto",
        background: theme.modern.panel,
        borderTop: `1px solid ${theme.modern.line}`,
    },

    topinfo: {
        flex: "none",
        display: "grid",
        alignItems: "stretch",
        gridTemplateColumns: "minmax(232px,0.95fr) minmax(300px,1.2fr) minmax(330px,1.55fr)",
        gap: "7px",
        padding: 7,
        // hard ceiling so the trace always keeps usable height; each card
        // scrolls internally past this point
        maxHeight: "44vh",
        background: theme.modern.bg,
        borderBottom: `1px solid ${theme.modern.line}`,

        "@media (max-width:1040px)": {
            gridTemplateColumns: "1fr 1fr",
            "& > *:last-child": {gridColumn: "1 / -1"},
        },
        "@media (max-width:720px)": {gridTemplateColumns: "1fr"},
        "@media (max-width:820px)": {maxHeight: "none"},
    },
    /**
     * The ceiling raised while the findings field is showing a whole report.
     *
     * Raised, not removed: a band free to grow would let a long report push the
     * trace off the screen entirely, and the point of reading the findings back
     * is to check them against it.
     *
     * 64vh, not more, because this band does NOT start at the top of the
     * viewport — the app bar and the toolbar sit above it, about 94 px. At 74vh
     * a forty-line report left 145 px of trace, under one lead. 64vh keeps
     * roughly 230 px, and past that the field scrolls as it did before.
     *
     * MUST stay declared after `topinfo` — JSS emits in source order and these
     * two have equal specificity, so `maxHeight: 44vh` would otherwise win.
     */
    /** Holds the findings card's place in the band. */
    dispo_cell: {
        display: "flex",
        minWidth: 0,
        minHeight: 0,
    },
    /**
     * Expanded, the findings card hangs out of the band instead of stretching
     * it.
     *
     * Growing the band was the obvious move and the wrong one: the band sizes
     * its row to its tallest card and stretches the others to match, so
     * expanding the findings pushed the WHOLE trace down and padded Patient and
     * Measurements out with 183 px and 139 px of blank card.
     *
     * Taking the card out of flow leaves the row to the other two, so the band
     * keeps its height and the trace stays where it is. The card overhangs
     * downward across its own column only — the left two thirds of every lead
     * stay visible at full height, and only the top of the right third is
     * covered while the findings are being read.
     *
     * The cell itself then has no height, which is the point; `z-index` has to
     * clear the caliper layer at 4.
     */
    dispo_cell__expanded: {
        position: "relative",

        "& > *": {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "auto",
            maxHeight: "72vh",
            zIndex: 20,
        },
    },
    /**
     * Shared by `topinfo` and `rail` — both vanish when the panel is toggled
     * off. MUST stay declared after both of them: JSS emits rules in source
     * order, the two class names have equal specificity, and if this came first
     * their `display: grid` / `display: flex` would win and the Panel button
     * would do nothing at all.
     */
    collapsed: {
        display: "none",
    },
    /**
     * Non-scrolling frame around the stage. The caliper hint and the measure
     * bar anchor to THIS box; putting them inside `stage` would let them scroll
     * off with the trace, which is the one place they must never go.
     */
    stage_wrap: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        minWidth: 0,
        minHeight: 0,

        "@media (max-width:820px)": {
            flex: "none",
            height: "60vh",
            minHeight: 320,
        },
    },
    stage: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        minHeight: 0,
        overflow: "auto",
        background: theme.modern.paper,
    },
    /**
     * Dragging the trace pans it — see `useEcgZoom`. Only while the calipers
     * are off: with them on the same drag is a measurement, and advertising a
     * grab hand over a crosshair would be a lie.
     */
    stage__pan: {
        cursor: "grab",

        "&:active": {cursor: "grabbing"},
    },
    caliper_hint: {
        position: "absolute",
        left: 12,
        bottom: 12,
        zIndex: 4,
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        background: theme.modern.hintBg,
        border: `1px solid ${theme.modern.line}`,
        borderRadius: 99,
        padding: "4px 12px",
        font: `500 11px ${theme.modern.sans}`,
        color: theme.modern.ink2,
        boxShadow: theme.modern.shadow,
        pointerEvents: "none",
        maxWidth: "calc(100% - 24px)",
    },
    stage_message: {
        flex: 1,
        display: "grid",
        placeItems: "center",
        font: `500 12px ${theme.modern.mono}`,
        color: theme.modern.ink3,
        padding: 24,
    },

    // ---- app bar meta block (rendered as <ModernAppBar/> children) ----
    recmeta: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        minWidth: 0,
        "&&": {flex: "1 1 auto"},
        "@media (max-width:640px)": {
            flexWrap: "wrap",
            gap: "6px",
            "&&": {order: 5, flex: "1 1 100%"},
        },
    },
    fname: {
        font: `600 13px ${theme.modern.mono}`,
        color: theme.modern.ink,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: 260,
        minWidth: 0,
        flex: "1 1 auto",

        "@media (max-width:640px)": {
            maxWidth: "100%",
            fontSize: 12,
        },
    },
    chip: {
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        height: 22,
        padding: "0 9px",
        borderRadius: 99,
        font: `600 10.5px ${theme.modern.sans}`,
        letterSpacing: ".04em",
        textTransform: "uppercase",
        border: "1px solid transparent",
        whiteSpace: "nowrap",
        background: theme.modern.panel2,
        color: theme.modern.ink2,
        borderColor: theme.modern.line,
    },
    chip__new: {
        background: theme.modern.accentSoft,
        borderColor: theme.modern.accentLine,
        color: theme.modern.accent,
    },
    chip__progress: {
        background: theme.modern.warnSoft,
        borderColor: theme.modern.resWarnBorder,
        color: theme.modern.warn,
    },
    chip__approved: {
        background: theme.modern.okSoft,
        borderColor: theme.modern.resOkBorder,
        color: theme.modern.ok,
    },
    chip__pediatric: {
        background: theme.modern.critSoft,
        borderColor: theme.modern.crit,
        color: theme.modern.crit,
    },
    dot: {
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: "currentColor",
        flex: "none",
    },

    // ---- loading / empty ----
    centered: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "14px",
        padding: 24,
        textAlign: "center",
    },
    centered_title: {
        font: `600 15px ${theme.modern.sans}`,
        color: theme.modern.ink,
    },
    centered_note: {
        font: `500 12px ${theme.modern.mono}`,
        color: theme.modern.ink3,
    },
    cta: {
        height: 32,
        padding: "0 16px",
        borderRadius: 8,
        border: `1px solid ${theme.modern.accent}`,
        background: theme.modern.accent,
        color: theme.modern.onAccent,
        font: `600 12px ${theme.modern.sans}`,
        cursor: "pointer",

        "&:hover": {filter: "brightness(1.06)"},
        "&:active": {filter: "brightness(.94)"},

        [COARSE]: {height: "auto", minHeight: TOUCH_MIN, padding: "0 20px"},
    },
}))
